"use client";

import React, { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScoreDistributionBar } from "./ScoreDistributionBar";
import { GeneralEntranceRequirements, ProgrammeEntranceRequirements } from "./EntranceRequirements";
import { ScoreBreakdownList } from "./ScoreBreakdownList";
import { ScoringFomula } from "./ScoringFomula";
import { HistoricScores } from "./HistoricScores";
import { CutOffMarks } from "./CutOffMarks";
import { ProgrammeResult } from "./ResultsTable";
import { MoreInformation } from "./MoreInformation";
import { useLanguage } from "@/contexts/LanguageContext";
import { getSubjectCategory } from "@/lib/utils/subjectCategories";
import { getUniversityKey } from '../common';
import { CORE_SUBJECTS } from '../common';
import type { ScoreTriple } from "../calculator/calculator";
import {
  getGradeValues,
  calcSubjectMaxScore,
  calcBestScore,
  calculateDetailedScore,
  selectBestSubjects,
  doesInstitutionScoreCsd,
  check_dse_results,
} from "../calculator/calculator";
import { PROGRAMMES_DATA, INSTITUTIONS_DATA } from "../calculator/data";
import { ScoreBadge } from "./ScoreBadge";
import { YourScores } from "./YourScores";
import { TitleBar } from "./TitleBar";
import { getCategoryCLanguageScore } from "@/lib/utils/scoreCalculatorAdv";

interface Props {
  result: ProgrammeResult | null;
  isOpen: boolean;
  onClose: () => void;
  selectedScoreType: 'min' | 'current' | 'max';
  onScoreTypeChange: (type: 'min' | 'current' | 'max') => void;
  scores: Record<string, ScoreTriple>;
  selectedElectives: string[];
}

const stripJSPrefix = (code: string): string => {
  return code.replace(/^JS/, '');
};

function hasValidAdmissionScores(admissionHistory: { year: string; LQ: string | number; MED: string | number; UQ: string | number; HA?: string | number }[]): boolean {
  if (!admissionHistory || admissionHistory.length === 0) return false;

  return admissionHistory.some(score => {
    const hasLQ = score.LQ !== "N/A" && score.LQ !== 0;
    const hasMED = score.MED !== "N/A" && score.MED !== 0;
    const hasUQ = score.UQ !== "N/A" && score.UQ !== 0;
    const hasHA = score.HA !== undefined && score.HA !== "N/A" && score.HA !== 0;
    return hasLQ || hasMED || hasUQ || hasHA;
  });
}

export const ProgrammeDetailsDialog: React.FC<Props> = ({
  result,
  isOpen,
  onClose,
  selectedScoreType,
  onScoreTypeChange,
  scores,
  selectedElectives,
}) => {
  const { language, translate } = useLanguage();

  // Compute scores dynamically from live `scores` so the header always reflects
  // the grades currently entered on the input page.
  // These useMemo hooks must be before the early return to satisfy Rules of Hooks.
  const _jupasCodeForScores = result ? result.jupas_id.replace(/^JS/, '') : '';
  const _activeSubjectsForScores = result
    ? [...CORE_SUBJECTS.map(s => s.subject), ...selectedElectives]
    : [];

  // Filter scores to ONLY include active subjects (CORE + selectedElectives).
  // This prevents orphaned entries (subjects removed from electives but still
  // present in scores from saved preferences) from inflating the calculated score.
  const _activeScores = useMemo(() => {
    const activeSet = new Set(_activeSubjectsForScores);
    return Object.fromEntries(
      Object.entries(scores).filter(([subject]) => activeSet.has(subject))
    );
  }, [scores, _activeSubjectsForScores]);

  const _hasMin = _activeSubjectsForScores.some(s =>
    s !== 'Citizenship and Social Development' &&
    Boolean(scores[s]?.min) && scores[s]?.min !== 'N/A',
  );
  const _hasMax = _activeSubjectsForScores.some(s =>
    s !== 'Citizenship and Social Development' &&
    Boolean(scores[s]?.max) && scores[s]?.max !== 'N/A',
  );
  const liveCurrentScore = useMemo(
    () => {
      if (!result) return 0;
      const computed = calcBestScore(_jupasCodeForScores, _activeScores, PROGRAMMES_DATA as any, 'current');
      const allCurrentGrades = Object.fromEntries(
        Object.entries(_activeScores).map(([k, v]) => [k, v?.current])
      );
      console.log('[ProgrammeDetailsDialog] liveCurrentScore debug', {
        jupasCode: _jupasCodeForScores,
        allCurrentGrades,
        activeScoresKeys: Object.keys(_activeScores),
        computed,
        fallback: result.currentScore,
        using: computed || result.currentScore,
      });
      return computed || result.currentScore;
    },
    [_jupasCodeForScores, _activeScores, result],
  );
  const dialogMinScore = useMemo(
    () => (_hasMin && result)
      ? calcBestScore(_jupasCodeForScores, _activeScores, PROGRAMMES_DATA as any, 'min')
      : 0,
    [_jupasCodeForScores, _activeScores, _hasMin, result],
  );
  const dialogMaxScore = useMemo(
    () => (_hasMax && result)
      ? calcBestScore(_jupasCodeForScores, _activeScores, PROGRAMMES_DATA as any, 'max')
      : 0,
    [_jupasCodeForScores, _activeScores, _hasMax, result],
  );

  const safeJupasId = result?.jupas_id || '';
  const safeInstKey = result?.instKey || '';
  const jupasCode = stripJSPrefix(safeJupasId);
  const programData = useMemo(
    () => PROGRAMMES_DATA.find(p => p.jupas_id === jupasCode),
    [jupasCode]
  );

  const handleInteractOutside = (event: Event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    if (target.closest('[data-website-badge-panel="true"]')) {
      event.preventDefault();
    }
  };

  let programmeGradeValues = programData?.grade_values;
  if (!programmeGradeValues && programData?.scoring_formula?.grade_values) {
    programmeGradeValues = programData.scoring_formula.grade_values;
  }

  const gradeValues = useMemo(() => {
    if (!result || !safeInstKey || !programData) {
      return {} as Record<string, number>;
    }

    return getGradeValues(safeInstKey, jupasCode, programmeGradeValues);
  }, [jupasCode, programData, programmeGradeValues, result, safeInstKey]);
  const activeSubjects = [...CORE_SUBJECTS.map(subject => subject.subject), ...selectedElectives];
  const hasEnteredMinScores = _hasMin;
  const hasEnteredMaxScores = _hasMax;
  const effectiveSelectedScoreType =
    selectedScoreType === 'min' && !hasEnteredMinScores
      ? 'current'
      : selectedScoreType === 'max' && !hasEnteredMaxScores
        ? 'current'
        : selectedScoreType;

  const getGradeForSubject = (subjectName: string) => {
    return scores[subjectName]?.[effectiveSelectedScoreType] || '';
  };

  const getBaseScore = (subjectName: string, grade: string): number => {
    if (["French", "German", "Spanish", "Korean", "Japanese", "Urdu"].includes(subjectName)) {
      return getCategoryCLanguageScore(grade, safeInstKey, subjectName);
    }
    return gradeValues[grade] || 0;
  };

  const liveCurrentBreakdown = useMemo(() => {
    if (!result || !programData) {
      return [];
    }

    return calculateDetailedScore(
      _activeScores,
      selectedElectives,
      programData,
      safeInstKey,
      'current'
    );
  }, [_activeScores, programData, safeInstKey, selectedElectives]);

  const liveEligibility = useMemo(() => {
    if (!result || !programData) {
      return null;
    }

    const dseResults = Object.fromEntries(
      Object.entries(_activeScores)
        .map(([subject, triple]) => [subject, triple?.current])
        .filter(([, grade]) => grade && grade !== 'N/A')
    );

    if (Object.keys(dseResults).length === 0) {
      return null;
    }

    return check_dse_results({
      jupas_id: jupasCode,
      dse_results: dseResults,
      programs: PROGRAMMES_DATA as any[],
      program: programData as any,
      fastMode: false,
    } as any);
  }, [_activeScores, jupasCode, programData, result]);

  if (!result) return null;

  const rankedSubjects = selectBestSubjects(programData, result.jupas_id, liveCurrentBreakdown).map(subject => {
    const grade = getGradeForSubject(subject.subject);
    const baseScore = getBaseScore(subject.subject, grade);
    const score = baseScore * (subject.weighting || 1);
    const maxScore = calcSubjectMaxScore(
      subject.subject,
      result.instKey || '',
      jupasCode,
      programmeGradeValues,
      subject.weighting || 1,
    );

    return {
      ...subject,
      grade,
      baseScore,
      score,
      maxScore,
    };
  });

  const displayInstKey = getUniversityKey(result.jupas_id);
  const displayedSubjects = rankedSubjects?.filter(subject =>
    doesInstitutionScoreCsd(displayInstKey) || subject.subject !== "Citizenship and Social Development"
  ) || [];
  const isPolyU = jupasCode.startsWith('3');
  const polyuNoCategoryB = [
    '3036', '3220', '3250', '3214', '3140', '3791', '3211', '3003', '3320', '3739',
    '3006', '3868', '3004', '3223', '3170', '3375', '3070', '3310', '3007', '3000',
    '3243', '3080', '3478', '3337', '3648', '3624', '3290', '3030', '3636', '3612',
    '3130', '3242', '3789',
  ];

  const categoryBCount = isPolyU
    ? (polyuNoCategoryB.includes(jupasCode)
      ? 0
      : (result.subjectWeightings
        ? Object.keys(result.subjectWeightings).filter(subject => getSubjectCategory(subject) === 'B').length
        : 0))
    : 0;

  const titleBarInstKey = displayInstKey;
  const progData = PROGRAMMES_DATA.find((p: any) => p.jupas_id === jupasCode);
  const instData = titleBarInstKey ? (INSTITUTIONS_DATA as any)[titleBarInstKey] : null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        id="dialog-prog"
        className="w-[calc(100vw-2rem)] max-w-[768px] max-h-[90vh] overflow-y-auto overflow-x-hidden bg-white p-3 sm:p-6"
        aria-describedby="dialog-description"
        onInteractOutside={handleInteractOutside}
      >
        <DialogHeader>
          <DialogTitle className="sr-only">
            JS{result.jupas_id} - {result.programmeName}
          </DialogTitle>
          
          <TitleBar
            jupasId={result.jupas_id}
            programmeName={result.programmeName}
            progData={progData}
            instData={instData}
            ger={result.ger}
            per={result.per}
            translate={translate}
            userScores={_activeScores}
            activeSubjects={_activeSubjectsForScores}
            programmes={PROGRAMMES_DATA}
            logoClassName="w-8 h-8 sm:w-10 sm:h-10 rounded flex-shrink-0"
          />
          <span id="dialog-description" className="sr-only">Programme details and admission information</span>
        </DialogHeader>

        <div className="space-y-4 sm:space-y-6 mt-3 sm:mt-4 overflow-x-hidden">
          {result.programmeNote && (
            <div className="p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold mb-2 text-blue-900 text-xs sm:text-sm uppercase tracking-wide">
                Programme Note
              </h4>
              <div className="text-sm text-blue-800 whitespace-pre-wrap leading-relaxed">
                {result.programmeNote}
              </div>
            </div>
          )}

          <YourScores
            currentScore={liveCurrentScore}
            highestAttainableScore={result.highestAttainableScore}
            translate={translate}
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div className="p-3 sm:p-4 bg-slate-50 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-semibold text-slate-700 text-xs sm:text-sm">
                  {translate('scoreDistribution')}
                </h4>
                {hasValidAdmissionScores(result.admissionHistory) && (
                  <ScoreBadge
                    jupas_id={result.jupas_id}
                    userScores={_activeScores}
                    activeSubjects={_activeSubjectsForScores}
                    programs={PROGRAMMES_DATA}
                    ger={liveEligibility?.ger ?? result.ger}
                    per={liveEligibility?.per ?? result.per}
                  />
                )}
              </div>

              <ScoreDistributionBar
                result={result}
                language={language}
                admissionHistory={result.admissionHistory}
                userMinScore={dialogMinScore}
                userCurrentScore={liveCurrentScore}
                userMaxScore={dialogMaxScore}
                highestAttainableScore={result.highestAttainableScore}
                selectedScoreType={effectiveSelectedScoreType}
                showPastYears={1}
                recommendationRate={result.recommendationRate}
                scoreBreakdown={displayedSubjects}
                ger={liveEligibility?.ger ?? result.ger}
                per={liveEligibility?.per ?? result.per}
              />

              <HistoricScores
                admissionHistory={result.admissionHistory}
                translate={translate}
              />
            </div>

            <ScoreBreakdownList
              jupas_id={result.jupas_id}
              userScores={_activeScores}
              activeSubjects={_activeSubjectsForScores}
              selectedScoreType={effectiveSelectedScoreType}
              translate={translate}
            />
          </div>
          {
          // <CutOffMarks
          //   subjects={displayedSubjects.map(subject => subject.subject)}
          //   selectedGrades={Object.fromEntries(
          //     displayedSubjects.map(subject => [subject.subject, subject.grade])
          //   )}
          //   translate={translate}
          // />
          }
          
          {(() => {
            const getAllowedCategories = (): ('A' | 'B' | 'C')[] => {
              const instKey = getUniversityKey(result.jupas_id);
              const institutionData = instKey ? (INSTITUTIONS_DATA as any)[instKey] : null;
              const maxPerCategory = institutionData?.max_subjects_per_category || { A: -1, B: 0, C: 1 };

              return (['A', 'B', 'C'] as const).filter(category => maxPerCategory[category] !== 0);
            };

            const allowedCategories = getAllowedCategories();

            return (
              <>
                <GeneralEntranceRequirements
                  ger={(liveEligibility?.ger ?? result.ger) as any}
                  userScores={_activeScores}
                  selectedScoreType={effectiveSelectedScoreType}
                  allowedCategories={allowedCategories}
                  program={programData}
                />

                <ProgrammeEntranceRequirements
                  per={(liveEligibility?.per ?? result.per) as any}
                  userScores={_activeScores}
                  selectedScoreType={effectiveSelectedScoreType}
                  allowedCategories={allowedCategories}
                  program={programData}
                />
              </>
            );
          })()}

          {programData && (
            <ScoringFomula
              program={programData}
              matchedSubjects={(liveCurrentBreakdown as any) || []}
              userScores={_activeScores}
              selectedScoreType={effectiveSelectedScoreType}
            />
          )}

          <MoreInformation
            universityInfo={result.universityInfo}
            progUrl={result.prog_url}
            jupasUrl={result.jupas_url}
            firstYearIntake={result.first_year_intake}
            programmeNote={result.programmeNote}
            subjectMER={result.subjectMER}
            translate={translate}
            stripJSPrefix={stripJSPrefix}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};